import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../../../utils/logger';
import {
  resetSessionBoundaryData,
  setSessionStart,
} from '../sessionBoundary/sessionBoundary.slice';
import {
  AgentMode,
  DEFAULT_AGENT_MODE,
  SLICE_NAME,
} from './agentMode.constants';

export interface AgentModeState {
  mode: AgentMode;
}

export const initialState: AgentModeState = {
  mode: DEFAULT_AGENT_MODE,
};

/**
 * Back to the silent default. Called at every point a *new* HITL session begins
 * — never at session end and never on `car_exit`: a takeover lets the car leave
 * while the same session's order is still being finished, and resetting the
 * control state there is exactly what caused the dropped-order + cart-revert
 * incident (see the reasoning block in CustomerPulse).
 *
 * "A new session begins" is deliberately taken from the same three signals the
 * rest of the app already uses, rather than re-derived from the message stream:
 *
 *   - `setSessionStart` — the task-router flow.
 *   - `resetSessionBoundaryData` — restaurant change and the task-router WS
 *     teardown, both between orders.
 *   - `resetAgentMode` — dispatched from useSessionStatus beside `resetAI()`
 *     and `resetHypothesisFrame()`, which is the single-restaurant flow's
 *     session start.
 *
 * An earlier draft watched `messageReceived` for `car_enter` itself and needed
 * an `isCartEmpty` flag plumbed through the message payload to avoid resetting
 * while the previous order was still being finished. That work is already done
 * upstream: sessionBoundary only starts a session when `!state.sessionId`, so an
 * order still in flight keeps the old session and none of these three fire. The
 * flag, its two middleware call sites and a fourth definition of "the cart is
 * empty" all went away with it.
 */
const resetToDefault = (state: AgentModeState) => {
  if (state.mode !== DEFAULT_AGENT_MODE) {
    logger.info(`Agent mode reset to ${DEFAULT_AGENT_MODE} on session start`);
  }
  state.mode = DEFAULT_AGENT_MODE;
};

export const agentModeSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    /**
     * The whole of what pressing the switch does.
     *
     * It emits **nothing**. The mode is permission — it unlocks the agent's
     * surfaces — while the wire events stay where they already are, on the
     * agent's real actions (`agent_interception`, typed by what was done, plus
     * the POS's one-shot `agent_first_interception`).
     *
     * That asymmetry is the point: emitting here would fail *dangerous*, since
     * an accidental press would silence the AI and leave nobody serving the
     * customer. Emitting on the first real action fails *safe* — the AI keeps
     * working until a human actually does something.
     */
    setAgentMode: (state, action: PayloadAction<AgentMode>) => {
      // ONE-WAY. Choosing Intercept is the decision, and it stands for the rest
      // of the session.
      //
      // An earlier draft let the agent walk back out of Intercept until they had
      // actually acted, tracked by an `engaged` latch set on the first
      // `agent_interception`. It bought a narrow undo — a mis-press, corrected
      // before doing anything — and charged for it everywhere else: two
      // indistinguishable Intercept states (armed and engaged) that every later
      // consumer had to know the difference between, a second piece of state to
      // reset, and a switch that could disable its own segments mid-order.
      //
      // The undo was worth less than it looked. Selecting a mode emits nothing,
      // so a mis-press reaches neither the customer nor the POS; all it costs is
      // the Shadow safety net for the current order, and the agent can still
      // simply not act. One state, one rule, and no consumer has to ask how far
      // into Intercept the agent is.
      if (state.mode === AgentMode.intercept) return;
      state.mode = action.payload;
    },

    /**
     * Session start, single-restaurant flow. sessionBoundary sets
     * `sessionStatus` to `start` inline on `car_enter` without dispatching
     * `setSessionStart`, so that path needs its own signal; useSessionStatus
     * dispatches this next to the AI slice's own reset, so the mode and the AI
     * control state it sits on top of can never disagree about which session
     * they are in.
     */
    resetAgentMode: resetToDefault,
  },
  extraReducers: (builder) => {
    // The task-router flow.
    builder.addCase(setSessionStart, resetToDefault);

    // Restaurant change and the task-router WS teardown; both between orders.
    builder.addCase(resetSessionBoundaryData, resetToDefault);
  },
});

export const { setAgentMode, resetAgentMode } = agentModeSlice.actions;

export default agentModeSlice.reducer;
